import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, downloadFile, gbp } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";
import { BTN } from "./AdminInvoices";
import { EMPTY_SOR, SorForm, sorFromRecord, sorNet } from "./SorForm";

const download = (no, fmt) => downloadFile(`/admin/sor/${no}/${fmt}`, `Cirencester-4x4-SOR-Agreement-${no}.${fmt}`).catch((e) => toast.error(apiError(e)));

const BlankSor = () => (
  <section className="mt-8 border border-stone bg-paper p-6">
    <h2 className="mb-4 font-serif text-2xl">Blank Sale or Return Agreement</h2>
    <div className="flex flex-wrap items-center justify-between gap-4 border border-stone p-4" data-testid="doc-sor-agreement">
      <p className="max-w-2xl text-sm text-charcoal/75">For selling a vehicle on its owner's behalf. Covers the advertised price, the owner's minimum return, our fee, the agreement period, withdrawal, finance, insurance, owner declarations, handover and a settlement statement. Print it blank to complete by hand.</p>
      <div className="flex gap-2">
        {[["Word", "docx"], ["PDF", "pdf"]].map(([l, ext]) => (
          <a key={ext} href={`/docs/Cirencester-4x4-Sale-or-Return-Agreement.${ext}`} download data-testid={`doc-sor-agreement-${ext}`} className={BTN}>Download {l}</a>
        ))}
      </div>
    </div>
  </section>
);

const SorRow = ({ s, onEdit }) => (
  <li className={`flex flex-wrap items-center justify-between gap-3 border-b border-stone py-4 ${["Withdrawn", "Returned"].includes(s.status) ? "opacity-60" : ""}`} data-testid={`sor-row-${s.sor_no}`}>
    <div className="min-w-0">
      <p className="text-sm font-semibold">{s.sor_no} <span className="font-normal text-charcoal/70">· {s.agreement_date} · {s.status}</span></p>
      <p className="text-sm">{s.owner_name} · {[s.make, s.model, s.derivative].filter(Boolean).join(" ")}{s.registration ? ` (${s.registration})` : ""}</p>
      <p className="text-sm text-forest">
        {s.status === "Sold" && s.sale_price ? <><strong>Sold {gbp(s.sale_price)}</strong> · owner receives {gbp(sorNet(s))}</> : <>Advertised {s.advertised_price ? gbp(s.advertised_price) : "not set"} · owner minimum {gbp(s.minimum_return)}</>}
      </p>
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" className={BTN} onClick={() => download(s.sor_no, "pdf")} data-testid={`sor-pdf-${s.sor_no}`}>Agreement PDF</button>
      <button type="button" className={BTN} onClick={() => download(s.sor_no, "docx")} data-testid={`sor-docx-${s.sor_no}`}>Word</button>
      <button type="button" className={BTN} onClick={() => onEdit(s)} data-testid={`sor-edit-${s.sor_no}`}>Edit</button>
    </div>
  </li>
);

export default function AdminSor() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | {initial, sorNo}
  const load = useCallback(() => {
    api.get("/admin/sor").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const active = list.filter((s) => s.status === "Active").length;
  return (
    <div data-testid="admin-sor">
      <AdminTitle title="Sale or Return">
        {!editing && <button type="button" onClick={() => setEditing({ initial: EMPTY_SOR, sorNo: null })} className="btn btn-primary !py-3" data-testid="sor-new">Record an SOR</button>}
      </AdminTitle>
      <p className="mb-6 text-sm text-charcoal/75">Vehicles you sell on behalf of their owners. Each gets an agreement number (SOR-year-101 onwards) and a pre-filled Sale or Return Agreement. When a car sells, add the sale price to get the settlement statement. Private: only signed-in admins can see it.</p>
      {editing && (
        <div className="mb-8">
          <SorForm key={editing.sorNo || "new"} initial={editing.initial} sorNo={editing.sorNo}
            onSaved={(s) => { setEditing(null); load(); download(s.sor_no, "pdf"); }} onCancel={() => setEditing(null)} />
        </div>
      )}
      <section className="border border-stone bg-paper px-6 py-2">
        {list.length ? (
          <>
            <ul>{list.map((s) => <SorRow key={s.sor_no} s={s} onEdit={(x) => { setEditing({ initial: sorFromRecord(x), sorNo: x.sor_no }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />)}</ul>
            <p className="py-4 text-sm text-charcoal/75" data-testid="sor-summary">{list.length} agreement{list.length === 1 ? "" : "s"} · {active} active</p>
          </>
        ) : <p className="py-6 text-sm text-charcoal/70" data-testid="sor-empty">No sale or return agreements yet.</p>}
      </section>
      <BlankSor />
    </div>
  );
}
